//firebase.js
import { initializeApp } from "firebase/app";
import {
  getAuth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut,
  sendEmailVerification,
  updateProfile,
  updateEmail,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
  deleteUser,
} from "firebase/auth";
import { getDatabase, ref, get, set, update, onValue } from "firebase/database"; // Corrected import
import { getFunctions, httpsCallable } from "firebase/functions";
import {
  getFirestore,
  doc,
  getDoc,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { cacheService } from './cacheService';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getDatabase(app); // Realtime Database
const firestore = getFirestore(app); // Firestore Database
const functions = getFunctions(app);

// Function to check if a Firestore user document exists and create it if not
const checkAndCreateUserDoc = async (user) => {
  const userDocRef = doc(firestore, "users", user.uid);
  const userDocSnapshot = await getDoc(userDocRef);

  if (!userDocSnapshot.exists()) {
    console.log("Firestore user document not found, creating...");
    const initialData = {
      theme: "light", // Default theme
      favoriteTeam: null, // Placeholder for future functionality
      username: user.displayName,
      titles: [], // Placeholder for future functionality
    };
    await setDoc(userDocRef, initialData);
    console.log("Firestore user document created successfully!");
  } else {
    console.log("Firestore user document already exists.");
  }
};

export const fetchUserTitles = async (user, leagueId) => {
  if (!leagueId) {
    return [];
  }

  // Check cache first
  const cacheKey = cacheService.getCacheKey('userTitles', `${user.uid}_${leagueId}`);
  const cached = cacheService.get(cacheKey);
  if (cached) {
    // Background refresh for next time
    setTimeout(async () => {
      try {
        const memberDocRef = doc(firestore, 'leagues', leagueId, 'members', user.uid);
        const memberDoc = await getDoc(memberDocRef);
        if (memberDoc.exists()) {
          const titles = memberDoc.data().titles || [];
          cacheService.set(cacheKey, titles, 6 * 60 * 60 * 1000); // 6 hours
        }
      } catch (error) {
        console.warn('Background titles refresh failed:', error);
      }
    }, 100);
    
    return cached;
  }

  try {
    const memberDocRef = doc(firestore, 'leagues', leagueId, 'members', user.uid);
    const memberDoc = await getDoc(memberDocRef);

    if (memberDoc.exists()) {
      const titles = memberDoc.data().titles || [];
      // Cache for 6 hours (titles change when seasons end)
      cacheService.set(cacheKey, titles, 6 * 60 * 60 * 1000);
      return titles;
    }
  } catch (error) {
    console.error('Error fetching user titles:', error);
  }

  return [];
};

export const checkIsReigningChampion = (userTitles, currentSeason) => {
  if (!userTitles || userTitles.length === 0 || !currentSeason) {
    return false;
  }

  // Get the previous season (when the title would have been won)
  const [currentYear] = currentSeason.split('-');
  const previousSeason = `${parseInt(currentYear) - 1}-${currentYear}`;

  // Check if user's most recent title is from the previous season
  const mostRecentTitle = userTitles[userTitles.length - 1];
  return mostRecentTitle === previousSeason;
};

// Migration functions - call these once to migrate to new structure
export const runLeagueMembersMigration = async () => {
  try {
    console.log('Starting league members migration...');
    
    // Check if user is authenticated
    if (!auth.currentUser) {
      throw new Error('User not authenticated');
    }
    
    // Use Firebase SDK to call the Cloud Function
    const migrationFunction = httpsCallable(functions, 'migrateLeagueMembers');
    const result = await migrationFunction();
    
    console.log('Migration result:', result);
    return result.data;
  } catch (error) {
    console.error('Error running migration:', error);
    // Extract the actual error message from Firebase Functions error
    if (error.code && error.message) {
      throw new Error(error.message);
    }
    throw error;
  }
};

export const verifyMigrationResults = async () => {
  try {
    console.log('Verifying migration results...');
    
    // Check if user is authenticated
    if (!auth.currentUser) {
      throw new Error('User not authenticated');
    }
    
    // Use Firebase SDK to call the Cloud Function
    const verifyFunction = httpsCallable(functions, 'verifyMigration');
    const result = await verifyFunction();
    
    console.log('Verification result:', result);
    return result.data;
  } catch (error) {
    console.error('Error verifying migration:', error);
    // Extract the actual error message from Firebase Functions error
    if (error.code && error.message) {
      throw new Error(error.message);
    }
    throw error;
  }
};

// Note: per-league prediction tables are created lazily, per season, by PredictionsTable.js
// (and on join by the joinLeague Cloud Function). The old global predictions/{season}/...
// table is no longer used, so there is nothing to seed on login/registration.

// Register a new user
const registerWithEmailAndPassword = async (
  name,
  email,
  password,
  seasonYear,
  nextYear
) => {
  try {
    const res = await createUserWithEmailAndPassword(auth, email, password);
    const user = res.user;
    await updateProfile(user, { displayName: name });
    await sendEmailVerification(user);
    alert(
      "Registration successful! Please check your email to verify your account."
    );

    // Ensure the user's Firestore document is created
    await checkAndCreateUserDoc(user);
  } catch (error) {
    console.error("Error during registration", error);
    alert(error.message);
  }
};

// Log in an existing user
const logInWithEmailAndPassword = async (
  email,
  password,
  seasonYear,
  nextYear
) => {
  try {
    const res = await signInWithEmailAndPassword(auth, email, password);
    const user = res.user;
    console.log("Logged in successfully!");

    // Ensure the user's Firestore document is created or updated
    await checkAndCreateUserDoc(user);
  } catch (error) {
    console.error("Error during login", error);
    alert("Invalid email or password");
  }
};

// Send password reset email
const sendPasswordReset = async (email) => {
  try {
    await sendPasswordResetEmail(auth, email);
    alert("Password reset link sent!");
  } catch (error) {
    console.error("Error sending password reset", error);
    alert(error.message);
  }
};
// Function to fetch user's favorite team from Firestore
const fetchFavoriteTeam = async (user) => {
  const userDocRef = doc(firestore, "users", user.uid);
  const userDocSnapshot = await getDoc(userDocRef);

  if (userDocSnapshot.exists()) {
    return userDocSnapshot.data().favoriteTeam;
  }
  return null;
};

// Function to save the favorite team and logo to Firestore
const saveFavoriteTeam = async (user, teamName, teamLogo) => {
  const userDocRef = doc(firestore, "users", user.uid);
  await updateDoc(userDocRef, {
    favoriteTeam: teamName,
    favoriteTeamLogo: teamLogo,
  });
  
  // Invalidate team data cache after update
  const cacheKey = cacheService.getCacheKey('teamData', user.uid);
  cacheService.remove(cacheKey);
};

// Function to fetch user's favorite team and logo from Firestore with caching
const fetchTeamData = async (user) => {
  // Check cache first
  const cacheKey = cacheService.getCacheKey('teamData', user.uid);
  const cached = cacheService.get(cacheKey);
  if (cached) {
    // Background refresh for next time
    setTimeout(async () => {
      try {
        const userDocRef = doc(firestore, "users", user.uid);
        const userDocSnapshot = await getDoc(userDocRef);
        if (userDocSnapshot.exists()) {
          const data = userDocSnapshot.data();
          const teamData = {
            favoriteTeam: data.favoriteTeam,
            favoriteTeamLogo: data.favoriteTeamLogo,
          };
          cacheService.set(cacheKey, teamData, 24 * 60 * 60 * 1000); // 24 hours
        }
      } catch (error) {
        console.warn('Background team data refresh failed:', error);
      }
    }, 100);
    
    return cached;
  }

  // Load from Firestore if no cache
  const userDocRef = doc(firestore, "users", user.uid);
  const userDocSnapshot = await getDoc(userDocRef);

  if (userDocSnapshot.exists()) {
    const data = userDocSnapshot.data();
    const teamData = {
      favoriteTeam: data.favoriteTeam,
      favoriteTeamLogo: data.favoriteTeamLogo,
    };
    // Cache for 24 hours
    cacheService.set(cacheKey, teamData, 24 * 60 * 60 * 1000);
    return teamData;
  }
  return { favoriteTeam: null, favoriteTeamLogo: null };
};

// Re-authenticate the user
export const reauthenticateUser = async (user, currentPassword) => {
  const cred = EmailAuthProvider.credential(user.email, currentPassword);
  await reauthenticateWithCredential(user, cred);
};

// Change user email
export const changeUserEmail = async (user, newEmail) => {
  await updateEmail(user, newEmail);
};

// Change user password
export const changeUserPassword = async (user, newPassword) => {
  await updatePassword(user, newPassword);
};

// Delete user account
export const deleteUserAccount = async (user) => {
  await deleteUser(user);
};

// Log out the user
const logout = () => {
  signOut(auth);
};

// League Management Functions
const createLeague = async (leagueData) => {
  try {
    console.log('Creating league with:', leagueData);
    
    // Check if user is authenticated
    if (!auth.currentUser) {
      throw new Error('User not authenticated');
    }
    
    // Use Firebase SDK to call the Cloud Function
    const createLeagueFunction = httpsCallable(functions, 'createLeague');
    const result = await createLeagueFunction(leagueData);
    
    console.log('Create league result:', result);
    return result.data;
  } catch (error) {
    console.error('Error creating league:', error);
    // Extract the actual error message from Firebase Functions error
    if (error.code && error.message) {
      throw new Error(error.message);
    }
    throw error;
  }
};

const joinLeague = async (leagueName, inviteCode) => {
  try {
    console.log('Joining league with:', { leagueName, inviteCode });
    
    // Validate input parameters
    if (!leagueName || typeof leagueName !== 'string' || leagueName.trim().length === 0) {
      throw new Error('League name is required and must be a non-empty string');
    }
    
    if (!inviteCode || typeof inviteCode !== 'string' || inviteCode.trim().length === 0) {
      throw new Error('Invite code is required and must be a non-empty string');
    }
    
    // Check if user is authenticated
    if (!auth.currentUser) {
      throw new Error('User not authenticated');
    }
    
    // Use Firebase SDK to call the Cloud Function
    const joinLeagueFunction = httpsCallable(functions, 'joinLeague');
    const result = await joinLeagueFunction({
      leagueName: leagueName.trim(),
      inviteCode: inviteCode.trim()
    });
    
    console.log('Cloud Function result:', result);
    return result.data;
  } catch (error) {
    console.error('Error joining league:', error);
    // Extract the actual error message from Firebase Functions error
    if (error.code && error.message) {
      throw new Error(error.message);
    }
    throw error;
  }
};

export {
  auth,
  db,
  firestore,
  getDatabase,
  ref,
  get,
  set,
  update,
  onValue, // Make sure these are exported
  registerWithEmailAndPassword,
  logInWithEmailAndPassword,
  sendPasswordReset,
  logout,
  checkAndCreateUserDoc,
  fetchFavoriteTeam,
  saveFavoriteTeam,
  fetchTeamData,
  createLeague,
  joinLeague,
};
